import React, { useState } from "react";
import axios from "axios";
import Link from "next/link";
import Head from "next/head";
import Image from "next/image";
import ErrorMessage from "@/components/ErrorMessage";
import { toast } from "react-toastify";
import { TOAST_ERROR, TOAST_SUCCESS } from "@/styles/toast";
import { useForm } from "react-hook-form";
import { useTranslation } from "next-i18next";
import { serverSideTranslations } from "next-i18next/serverSideTranslations";

const SignUpPage = () => {
  const { t } = useTranslation([
    "authentication",
    "common",
    "form",
    "pageTitle",
  ]);
  const [agreement, setAgreement] = useState("" as string);
  const [responseError, setResponseError] = useState(
    {} as { [key: string]: string }
  );

  const { register, handleSubmit } = useForm();

  const onSubmit = handleSubmit((data, e) => {
    if (data.signUpAgreement === false) {
      setAgreement("You need to agree on the terms of use and privay policy");
    } else {
      setAgreement("");

      axios
        .post("/auth/register", data)
        .then((res) => {
          toast.success(res.data.message, TOAST_SUCCESS);
          e?.target.reset();
        })
        .catch((error) => {
          toast.error(error.response.data.error, TOAST_ERROR);
          setResponseError(error.response.data.errors);
        });
    }
  });

  return (
    <>
      <Head>
        <title>{`${t("signUpPage", { ns: "pageTitle" })} ${t("baseTitle", {
          ns: "pageTitle",
        })}`}</title>
      </Head>
      <div className="flex min-h-full flex-col justify-center bg-background py-12 sm:px-6 lg:px-8">
        <div className="sm:mx-auto sm:w-full sm:max-w-md">
          <div className="mx-auto w-fit">
            <Link href={"/"}>
              <a>
                <Image
                  src={`/images/LeagueConnect_logo.webp`}
                  height={48}
                  width={48}
                  alt="LeagueConnect"
                />
              </a>
            </Link>
          </div>
          <h2 className="mt-2 text-center text-3xl font-extrabold text-white">
            {t("register", { ns: "common" })}
          </h2>
          <p className="mt-2 text-center text-sm text-gray-400">
            {t("alreadyRegistered", { ns: "authentication" })}{" "}
            <Link href="/sign-in">
              <a className="font-medium text-primary">
                {t("signInHere", { ns: "authentication" })}
              </a>
            </Link>
          </p>
        </div>

        <div className="mt-8 sm:mx-auto sm:w-full sm:max-w-md">
          <div className="py-8 px-4 shadow sm:rounded-lg sm:px-10">
            <form
              className="space-y-6"
              action="#"
              method="POST"
              onSubmit={onSubmit}
            >
              <div>
                <label
                  htmlFor="email"
                  className="block text-sm font-medium text-gray-300"
                >
                  {t("Email", { ns: "form" })}
                </label>
                <div className="mt-1">
                  <input
                    id="email"
                    type="email"
                    className="block w-full appearance-none rounded-md border border-gray-300 px-3 py-2 placeholder-gray-400 shadow-sm focus:border-primary focus:outline-none focus:ring-primary sm:text-sm"
                    {...register("email", { required: true })}
                  />
                  <ErrorMessage>{responseError["email"]}</ErrorMessage>
                </div>
              </div>
              <div>
                <label
                  htmlFor="password"
                  className="block text-sm font-medium text-gray-300"
                >
                  {t("label.password", { ns: "form" })}
                </label>
                <div className="mt-1">
                  <input
                    id="password"
                    type="password"
                    className="block w-full appearance-none rounded-md border border-gray-300 px-3 py-2 placeholder-gray-400 shadow-sm focus:border-primary focus:outline-none focus:ring-primary sm:text-sm"
                    {...register("password", { required: true })}
                  />
                  <ErrorMessage>{responseError["password"]}</ErrorMessage>
                </div>
              </div>
              <div>
                <label
                  htmlFor="repeat-password"
                  className="block text-sm font-medium text-gray-300"
                >
                  {t("label.confirmPassword", { ns: "form" })}
                </label>
                <div className="mt-1">
                  <input
                    id="confirmPassword"
                    type="password"
                    className="block w-full appearance-none rounded-md border border-gray-300 px-3 py-2 placeholder-gray-400 shadow-sm focus:border-primary focus:outline-none focus:ring-primary sm:text-sm"
                    {...register("confirmPassword", { required: true })}
                  />
                  <ErrorMessage>
                    {responseError["confirmPassword"]}
                  </ErrorMessage>
                </div>
              </div>
              <div className="flex items-center">
                <input
                  id="remember-me"
                  type="checkbox"
                  className="h-4 w-4 rounded border-gray-300 text-primary"
                  {...register("signUpAgreement")}
                />
                <label
                  htmlFor="remember-me"
                  className="ml-2 block text-xs text-gray-400"
                >
                  {t("signUpAgreement", { ns: "authentication" })}
                </label>
              </div>
              {agreement ? (
                <ErrorMessage>{responseError["signUpAgreement"]}</ErrorMessage>
              ) : null}
              <div>
                <button
                  type="submit"
                  className="flex w-full justify-center rounded-md border border-transparent bg-primary py-2 px-4 text-sm font-medium text-white shadow-sm focus:outline-none focus:ring-2 focus:ring-primary focus:ring-offset-2"
                >
                  {t("register", { ns: "common" })}
                </button>
              </div>
            </form>
          </div>
        </div>
      </div>
    </>
  );
};

export async function getStaticProps(props: { locale: string }) {
  return {
    props: {
      ...(await serverSideTranslations(props.locale, [
        "authentication",
        "common",
        "form",
        "pageTitle",
      ])),
    },
  };
}

export default SignUpPage;
